/**
 * Сховище значень за ключем для фільтрів сторінки модуля: живе в пам'яті сторінки й у localStorage під `storageKey` (недоступне сховище —
 * лише в пам'яті); підписники дізнаються про зміну одразу, інші вкладки — через подію storage. Значення, яке ще не записано, — `empty`.
 */
import type { IKeyedStore } from './IKeyedStore';

export function createKeyedStore<T>(storageKey: string, empty: T): IKeyedStore<T> {
  const listeners = new Set<() => void>();
  let cache: Record<string, T> | null = null;
  const load = (): Record<string, T> => {
    if (cache) return cache;
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '{}');
      cache = parsed && typeof parsed === 'object' ? (parsed as Record<string, T>) : {};
    } catch {
      cache = {};
    }
    return cache;
  };
  return {
    read: (key) => load()[key] ?? empty,
    write: (key, value) => {
      const next = { ...load() };
      if (value === null) delete next[key];
      else next[key] = value;
      cache = next;
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* приватний режим — лише в пам'яті сторінки */ }
      listeners.forEach((l) => l());
    },
    subscribe: (listener) => {
      const onStorage = (e: StorageEvent) => { if (e.key === storageKey) { cache = null; listener(); } };
      const win = typeof window === 'undefined' ? null : window;
      listeners.add(listener);
      win?.addEventListener('storage', onStorage);
      return () => { listeners.delete(listener); win?.removeEventListener('storage', onStorage); };
    },
  };
}
